/**
 * Label position calculation logic
 * 
 * Calculates where to place campaign zone labels so they:
 * 1. Are centered on the zone border (straddling the line)
 * 2. Stay within the visible viewport
 * 3. Don't overlap HUD elements
 * 4. Rotate 90° CCW on vertical edges (left/right)
 * 
 * IMPORTANT: 
 * - Zone bounds and viewport are in CANVAS coordinates
 * - Label dimensions (LABEL_WIDTH, LABEL_HEIGHT) are in SCREEN pixels
 * - We must convert label dimensions to canvas coords by dividing by zoom
 * - Returns CENTER coordinates in canvas coords
 */

import {
  DEBUG_LABEL_POSITIONING,
  LABEL_WIDTH,
  LABEL_HEIGHT,
  CANDIDATE_STEP,
  MIN_VISIBLE_RATIO,
  EDGE_PRIORITY,
} from './constants';

import {
  getOverlappingExclusionZone,
  calculateVisibleRatio,
} from './geometry';

// Corner padding in screen pixels (will be converted to canvas coords)
const CORNER_PADDING_SCREEN = 8;

/**
 * Get the valid range for the label CENTER along a zone edge.
 * The range ensures:
 * 1. The label stays within the zone edge (doesn't protrude past corners)
 * 2. The label stays within the visible viewport
 * 
 * @param {Object} zoneBounds - Zone bounding box in canvas coords
 * @param {string} edge - Edge name
 * @param {Object} viewport - Viewport bounds in canvas coords
 * @param {number} labelHalfWidth - Half the label width in canvas coords
 * @param {number} labelHalfHeight - Half the label height in canvas coords
 * @param {number} zoom - Current zoom level
 */
function getVisibleEdgeRange(zoneBounds, edge, viewport, labelHalfWidth, labelHalfHeight, zoom = 1) {
  const isVertical = edge === 'left' || edge === 'right';
  
  // For both vertical and horizontal edges, the label's width becomes its length along the edge
  // (for vertical edges after 90° rotation, for horizontal edges naturally)
  const labelHalfLengthAlongEdge = labelHalfWidth;
  
  // Small padding from zone corners for aesthetics (converted to canvas coords)
  const cornerPadding = CORNER_PADDING_SCREEN / zoom;
  
  if (isVertical) {
    const edgeStart = zoneBounds.y;
    const edgeEnd = zoneBounds.y + zoneBounds.height;
    
    // The label CENTER must be:
    // 1. At least labelHalfLengthAlongEdge + cornerPadding from the zone edge start (so label doesn't protrude past top)
    // 2. At least labelHalfLengthAlongEdge + cornerPadding from the zone edge end (so label doesn't protrude past bottom)
    // 3. At least labelHalfLengthAlongEdge from viewport edges (so label stays in view)
    const minFromZone = edgeStart + labelHalfLengthAlongEdge + cornerPadding;
    const maxFromZone = edgeEnd - labelHalfLengthAlongEdge - cornerPadding;
    const minFromViewport = viewport.y + labelHalfLengthAlongEdge;
    const maxFromViewport = viewport.bottom - labelHalfLengthAlongEdge;
    
    const visibleStart = Math.max(minFromZone, minFromViewport);
    const visibleEnd = Math.min(maxFromZone, maxFromViewport);
    
    if (visibleStart >= visibleEnd) return null;
    return { start: visibleStart, end: visibleEnd, edgeStart, edgeEnd };
  } else {
    const edgeStart = zoneBounds.x;
    const edgeEnd = zoneBounds.x + zoneBounds.width;
    
    // Same logic for horizontal edges
    const minFromZone = edgeStart + labelHalfLengthAlongEdge + cornerPadding;
    const maxFromZone = edgeEnd - labelHalfLengthAlongEdge - cornerPadding;
    const minFromViewport = viewport.x + labelHalfLengthAlongEdge;
    const maxFromViewport = viewport.right - labelHalfLengthAlongEdge;
    
    const visibleStart = Math.max(minFromZone, minFromViewport);
    const visibleEnd = Math.min(maxFromZone, maxFromViewport);
    
    if (visibleStart >= visibleEnd) return null;
    return { start: visibleStart, end: visibleEnd, edgeStart, edgeEnd };
  }
}

/**
 * Calculate the visual bounds of a label given its CENTER position.
 * For vertical edges, the label is rotated 90° CCW, so visual dimensions swap.
 * 
 * @param {number} centerX - X coordinate of label center (canvas coords)
 * @param {number} centerY - Y coordinate of label center (canvas coords)
 * @param {string} edge - Which edge the label is on
 * @param {number} labelWidth - Label width in canvas coords
 * @param {number} labelHeight - Label height in canvas coords
 * @returns {Object} Visual bounds {x, y, width, height} in canvas coords
 */
function getVisualBounds(centerX, centerY, edge, labelWidth, labelHeight) {
  const isVertical = edge === 'left' || edge === 'right';
  
  if (isVertical) {
    // After 90° CCW rotation, visual dimensions swap
    return {
      x: centerX - labelHeight / 2,
      y: centerY - labelWidth / 2,
      width: labelHeight,
      height: labelWidth,
    };
  } else {
    return {
      x: centerX - labelWidth / 2,
      y: centerY - labelHeight / 2,
      width: labelWidth,
      height: labelHeight,
    };
  }
}

/**
 * Calculate label CENTER position for a specific edge and position along that edge.
 * The center should be ON the border line.
 */
function calculateCenterOnEdge(edge, posAlongEdge, zoneBounds, labelWidth, labelHeight) {
  let centerX, centerY;
  
  switch (edge) {
    case 'top':
      centerX = posAlongEdge;
      centerY = zoneBounds.y;
      break;
      
    case 'bottom':
      centerX = posAlongEdge;
      centerY = zoneBounds.y + zoneBounds.height;
      break;
      
    case 'left':
      centerX = zoneBounds.x;
      centerY = posAlongEdge;
      break;
      
    case 'right':
      centerX = zoneBounds.x + zoneBounds.width;
      centerY = posAlongEdge;
      break;
      
    default:
      centerX = zoneBounds.x + labelWidth / 2;
      centerY = zoneBounds.y;
  }
  
  return {
    centerX,
    centerY,
    visualRect: getVisualBounds(centerX, centerY, edge, labelWidth, labelHeight),
  };
}

/**
 * Generate candidate positions along an edge
 * 
 * NOTE: visibleRange.start and visibleRange.end already account for:
 * - The label's half-length (so label stays within zone edge)
 * - Corner padding (aesthetic spacing from zone corners)
 * - Viewport constraints (so label stays visible)
 */
function generateCandidates(visibleRange, edge, labelWidth) {
  // After rotation, vertical labels take up labelWidth along the edge (as height)
  const labelLengthAlongEdge = labelWidth;
  
  const availableLength = visibleRange.end - visibleRange.start;
  if (availableLength < labelLengthAlongEdge * 0.3) {
    return [];
  }
  
  // visibleRange.start and visibleRange.end are already the valid range for label CENTER
  const minPos = visibleRange.start;
  const maxPos = visibleRange.end;
  
  const candidates = [];
  
  if (minPos > maxPos) {
    // Not enough room - try center of available range
    const center = (visibleRange.start + visibleRange.end) / 2;
    candidates.push(center);
  } else {
    for (let pos = minPos; pos <= maxPos; pos += CANDIDATE_STEP) {
      candidates.push(pos);
    }
    if (candidates.length === 0) {
      candidates.push(minPos);
    }
  }
  
  return candidates;
}

/**
 * Calculate the best label position for a campaign zone.
 * Returns CENTER coordinates in canvas coords.
 * 
 * @param {Object} zoneBounds - Zone bounding box in canvas coords
 * @param {Object} viewport - Viewport bounds in canvas coords
 * @param {Array} exclusionZones - HUD exclusion zones in canvas coords
 * @param {number} zoom - Current zoom level (to convert label dimensions)
 * @param {string} campaignName - For debug logging
 */
export function calculateLabelPosition(zoneBounds, viewport, exclusionZones, zoom = 1, campaignName = '') {
  const debugLog = DEBUG_LABEL_POSITIONING 
    ? (msg, data) => console.log(`[LabelPos:${campaignName}] ${msg}`, data || '')
    : () => {};
  
  // Convert label dimensions from screen pixels to canvas coords
  const labelWidth = LABEL_WIDTH / zoom;
  const labelHeight = LABEL_HEIGHT / zoom;
  const labelHalfWidth = labelWidth / 2;
  const labelHalfHeight = labelHeight / 2;
  
  debugLog('Calculating position', { 
    zoneBounds, 
    viewport: { 
      x: viewport.x.toFixed(0), 
      y: viewport.y.toFixed(0), 
      right: viewport.right.toFixed(0), 
      bottom: viewport.bottom.toFixed(0) 
    },
    labelDims: { width: labelWidth.toFixed(0), height: labelHeight.toFixed(0) }
  });
  
  for (const edge of EDGE_PRIORITY) {
    const visibleRange = getVisibleEdgeRange(zoneBounds, edge, viewport, labelHalfWidth, labelHalfHeight, zoom);
    
    if (!visibleRange) {
      debugLog(`Edge "${edge}": not visible or not enough room for label`);
      continue;
    }
    
    debugLog(`Edge "${edge}": visible range`, { 
      start: visibleRange.start.toFixed(0), 
      end: visibleRange.end.toFixed(0),
      length: (visibleRange.end - visibleRange.start).toFixed(0)
    });
    
    const candidates = generateCandidates(visibleRange, edge, labelWidth);
    
    if (candidates.length === 0) {
      debugLog(`Edge "${edge}": no valid candidates`);
      continue;
    }
    
    debugLog(`Edge "${edge}": ${candidates.length} candidates`);
    
    for (const posAlongEdge of candidates) {
      const { centerX, centerY, visualRect } = calculateCenterOnEdge(edge, posAlongEdge, zoneBounds, labelWidth, labelHeight);
      
      // Check visibility ratio
      const visibleRatio = calculateVisibleRatio(visualRect, viewport);
      if (visibleRatio < MIN_VISIBLE_RATIO) {
        debugLog(`  Candidate ${posAlongEdge.toFixed(0)}: only ${(visibleRatio * 100).toFixed(0)}% visible (need ${MIN_VISIBLE_RATIO * 100}%)`);
        continue;
      }
      
      // Check exclusion zones
      const overlappingZone = getOverlappingExclusionZone(visualRect, exclusionZones);
      if (overlappingZone) {
        debugLog(`  Candidate ${posAlongEdge.toFixed(0)}: overlaps ${overlappingZone} HUD`);
        continue;
      }
      
      debugLog(`  ✓ Candidate ${posAlongEdge.toFixed(0)}: ACCEPTED (${(visibleRatio * 100).toFixed(0)}% visible)`);
      
      return {
        centerX,
        centerY,
        edge,
        isVisible: true,
      };
    }
    
    debugLog(`Edge "${edge}": all candidates rejected`);
  }
  
  // Fallback
  debugLog('FALLBACK: No valid position found, using default');
  const labelWidth2 = LABEL_WIDTH / zoom;
  return {
    centerX: zoneBounds.x + 40 + labelWidth2 / 2,
    centerY: zoneBounds.y,
    edge: 'top',
    isVisible: false,
  };
}

/**
 * Check if a position is still valid (for hysteresis)
 */
export function isPositionStillValid(pos, zoneBounds, viewport, exclusionZones, zoom = 1) {
  if (!pos || !pos.isVisible) return false;
  
  const labelWidth = LABEL_WIDTH / zoom;
  const labelHeight = LABEL_HEIGHT / zoom;
  
  const visualRect = getVisualBounds(pos.centerX, pos.centerY, pos.edge, labelWidth, labelHeight);
  
  const visibleRatio = calculateVisibleRatio(visualRect, viewport);
  if (visibleRatio < MIN_VISIBLE_RATIO * 0.8) return false;
  
  if (getOverlappingExclusionZone(visualRect, exclusionZones)) return false;
  
  return true;
}
